import { useEffect, useRef, useState } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";

// Animate a number from 0 → target when it scrolls into view.
// Accepts values like "40+", "6 wks", "99.9%", "200+", "3 weeks".
// - Splits into numeric part + suffix (keeps prefix/suffix static).
// - Uses easeOutCirc, ~1.2s.
// - Skips animation entirely in reduced-motion (jumps to final value).
export default function Counter({ value, duration = 1.2 }) {
    const raw = String(value ?? "");
    const match = raw.match(/^([^\d]*)([\d.,]+)(.*)$/);
    const prefix = match ? match[1] : "";
    const numericStr = match ? match[2].replace(/,/g, "") : "0";
    const suffix = match ? match[3] : "";
    const target = Number.isFinite(parseFloat(numericStr)) ? parseFloat(numericStr) : 0;
    const hasDecimal = /\./.test(numericStr);
    const decimals = hasDecimal ? (numericStr.split(".")[1] || "").length : 0;

    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [display, setDisplay] = useState(0);
    const reduced = useReducedMotion();

    useEffect(() => {
        if (!inView) return;
        if (reduced || target === 0) {
            setDisplay(target);
            return;
        }
        const controls = animate(0, target, {
            duration,
            ease: [0, 0.55, 0.45, 1],
            onUpdate: (v) => setDisplay(v),
        });
        return () => controls.stop();
    }, [inView, target, duration, reduced]);

    const shown = hasDecimal ? display.toFixed(decimals) : Math.round(display).toString();
    return (
        <span ref={ref}>
            {prefix}
            {shown}
            {suffix}
        </span>
    );
}
